import React, {useEffect, useState} from 'react';
import {AppRegistry, View, StyleSheet, Platform} from 'react-native';
import Typography from '@shared/constants/Typography';
import {Provider} from 'react-redux';
import './index.web.css';
import './shared/localization/i18n';

// ─── Debounce window resize for React Native Web ─────────────────────────────
// RN Web's Dimensions module fires on every pixel of browser resize, causing
// useWindowDimensions() consumers to re-render (and re-trigger API calls) while
// the user is actively dragging the window edge.
// We replace window.addEventListener so that 'resize' listeners are wrapped with
// a 300 ms debounce — only the final size (after the user stops) is dispatched.
if (typeof window !== 'undefined') {
  const _addEvent = window.addEventListener.bind(window);
  const _removeEvent = window.removeEventListener.bind(window);
  const debouncedMap = new Map<
    EventListenerOrEventListenerObject,
    EventListenerOrEventListenerObject
  >();

  (window as any).addEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) {
    if (type === 'resize' && typeof listener === 'function') {
      let timer: ReturnType<typeof setTimeout>;
      const debounced: EventListener = (event: Event) => {
        clearTimeout(timer);
        timer = setTimeout(() => listener(event), 300);
      };
      debouncedMap.set(listener, debounced);
      return _addEvent(type, debounced, options);
    }
    return _addEvent(type, listener, options);
  };

  (window as any).removeEventListener = function (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ) {
    if (type === 'resize' && typeof listener === 'function') {
      const debounced = debouncedMap.get(listener);
      if (debounced) {
        debouncedMap.delete(listener);
        return _removeEvent(type, debounced, options);
      }
    }
    return _removeEvent(type, listener, options);
  };
}
// ─────────────────────────────────────────────────────────────────────────────
import App from './shared/navigation/AppNavigator.web';
import { getClientThemeTokens } from '@shared/theme/clientThemes';
import store from '@shared/store';
import {name as appName} from '../app.json';
import ErrorToast from '@shared/components/feedback/ErrorToast/ErrorToast';
import LoaderOverlay from '@shared/components/feedback/LoaderOverlay/LoaderOverlay';
import InactiveTabOverlay from '@shared/components/feedback/InactiveTabOverlay/InactiveTabOverlay';
import {useSelector} from 'react-redux';
import {RootState} from '@shared/store';
import {GlobalErrorModal} from '@shared/components';
import {tabManager} from '@shared/services/tabManager.web';
import NovaAI from '@shared/components/NovaAI/NovaAI';
import { navigationRef } from '@shared/navigation/navigationRef';

if (Platform.OS === 'web') {
  tabManager.init();
}

// ─── Initialise scrollbar CSS variables from design tokens ───────────────────
// Runs synchronously before React renders — no flash of hard-coded CSS values.
// --scrollbar-thumb : tokens.primarySoft from the default brand theme.
// --scrollbar-size  : Typography.borderWidth.borderWidth6 (fixed 6 px constant).
// useScrollbarTheme.web.ts updates --scrollbar-thumb on client-theme change.
if (typeof document !== 'undefined') {
  const defaultTokens = getClientThemeTokens('blue');
  document.documentElement.style.setProperty(
    '--scrollbar-thumb',
    defaultTokens.primarySoft,
  );
  document.documentElement.style.setProperty(
    '--scrollbar-size',
    `${Typography.borderWidth.borderWidth6}px`,
  );
}
// ─────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

const AppWithFeedback = () => {
  const {isVisible: isLoaderVisible} = useSelector(
    (state: RootState) => state.loader,
  );
  const {parentView: toastParentView} = useSelector(
    (state: RootState) => state.error,
  );
  const isNovaAIEnabled = useSelector(
    (state: RootState) => state.novaAI.isNovaAIEnabled,
  );
  const [currentScreen, setCurrentScreen] = useState<string>('');

  useEffect(() => {
    const unsubscribe = navigationRef.current?.addListener('state', () => {
      const routeName = navigationRef.current?.getCurrentRoute()?.name ?? '';
      setCurrentScreen(routeName);
    });
    return () => unsubscribe?.();
  }, []);

  return (
    <View style={styles.container}>
      <App />
      {(toastParentView ?? true) && <ErrorToast />}
      <LoaderOverlay visible={isLoaderVisible} />
      {Platform.OS === 'web' && <GlobalErrorModal />}
      {Platform.OS === 'web' && <InactiveTabOverlay />}
      {Platform.OS === 'web' && currentScreen === 'MainLayoutScreen' && isNovaAIEnabled && <NovaAI />}
    </View>
  );
};

const Root = () => (
  <Provider store={store}>
    <AppWithFeedback />
  </Provider>
);

// Register the app
AppRegistry.registerComponent(appName, () => Root);

// Run the app in web
AppRegistry.runApplication(appName, {
  rootTag: document.getElementById('root'),
});

